import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Layout from '../../components/Layout';
import SystemMonitorChart from './SystemMonitorChart';
import SystemMonitorUsers from './SystemMonitorUsers';
import {
  fetchSystemMonitor,
  blockSystemUser,
  unblockSystemUser,
  disconnectSystemUser,
  disconnectSystemConnection,
} from './systemMonitorApi';
import './SystemMonitor.scss';

const POLL_INTERVAL_MS = 3000;

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!(bytes > 0)) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const number = bytes / (1024 ** index);
  return `${number >= 100 ? number.toFixed(0) : number.toFixed(1)} ${units[index]}`;
};

const formatRate = (bytesPerSecond) => `${formatBytes(bytesPerSecond)}/s`;

const formatDuration = (seconds) => {
  const value = Math.max(0, Number(seconds || 0));
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  const minutes = Math.floor((value % 3600) / 60);

  if (days) return `${days}д ${hours}ч`;
  if (hours) return `${hours}ч ${minutes}м`;
  return `${minutes}м`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU');
};

const formatAgoSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const value = Math.max(0, Number(seconds || 0));
  if (value < 60) return `${Math.round(value)} сек назад`;
  if (value < 3600) return `${Math.floor(value / 60)} мин назад`;
  if (value < 86400) return `${Math.floor(value / 3600)} ч назад`;
  return `${Math.floor(value / 86400)} д назад`;
};

const statusTone = (ok, warning = false) => {
  if (warning) return 'warn';
  return ok ? 'good' : 'bad';
};

const StatCard = ({ label, value, sub, tone = 'neutral' }) => (
  <div className={`systemMonitor__statCard is-${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    {sub ? <small>{sub}</small> : null}
  </div>
);

const DetailRow = ({ label, value, tone }) => (
  <div className="systemMonitor__detailRow">
    <span>{label}</span>
    <strong className={tone ? `is-${tone}` : ''}>{value}</strong>
  </div>
);

const TopTable = ({ title, columns, rows = [], empty = 'Нет данных' }) => (
  <section className="systemMonitor__section">
    <div className="systemMonitor__sectionTitle">{title}</div>
    <div className="systemMonitor__tableWrap">
      <table className="systemMonitor__table">
        <thead>
          <tr>
            {columns.map((column) => <th key={column.key}>{column.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.key || `${title}-${index}`}>
              {columns.map((column) => (
                <td key={column.key} className={column.mono ? 'is-mono' : ''}>
                  {column.render ? column.render(row) : (row[column.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length ? <div className="systemMonitor__empty">{empty}</div> : null}
    </div>
  </section>
);

const SystemMonitorPage = () => {
  const currentUserId = useSelector((state) => state.auth?.user?._id || '');
  const [data, setData] = useState(null);
  const [historyMinutes, setHistoryMinutes] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [paused, setPaused] = useState(false);
  const requestInFlightRef = useRef(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;

    if (!silent) setLoading(true);

    try {
      const payload = await fetchSystemMonitor(historyMinutes);
      setData(payload);
      setError('');
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
        requestError?.message ||
        'Не удалось загрузить мониторинг'
      );
    } finally {
      requestInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [historyMinutes]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (paused) return undefined;

    const timer = window.setInterval(() => {
      if (!document.hidden) load({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [load, paused]);

  const runAction = useCallback(async (key, action) => {
    if (busyKey) return;
    setBusyKey(key);

    try {
      await action();
      await load({ silent: true });
    } catch (actionError) {
      window.alert(
        actionError?.response?.data?.message ||
        actionError?.message ||
        'Операция не выполнена'
      );
    } finally {
      setBusyKey('');
    }
  }, [busyKey, load]);

  const handleBlock = useCallback((user) => {
    const confirmed = window.confirm(`Заблокировать ${user.name || user.email || user.userId}?\n\nВсе активные socket-соединения будут закрыты, а текущий JWT перестанет работать.`);
    if (!confirmed) return;

    const reason = window.prompt('Причина блокировки (можно оставить пустой):', '') || '';
    runAction(user.userId, () => blockSystemUser(user.userId, reason));
  }, [runAction]);

  const handleUnblock = useCallback((user) => {
    const confirmed = window.confirm(`Разблокировать ${user.name || user.email || user.userId}?`);
    if (!confirmed) return;

    runAction(user.userId, () => unblockSystemUser(user.userId, 'Разблокировано из System Monitor'));
  }, [runAction]);

  const handleDisconnectUser = useCallback((user) => {
    const confirmed = window.confirm(`Отключить все текущие соединения ${user.name || user.email || user.userId}?\n\nАккаунт останется активным.`);
    if (!confirmed) return;

    runAction(`disconnect:${user.userId}`, () => disconnectSystemUser(user.userId));
  }, [runAction]);

  const handleDisconnectConnection = useCallback((connection) => {
    const confirmed = window.confirm(`Отключить это соединение?\n${connection.deviceLabel || ''} ${connection.browserLabel || ''}\n${connection.route || ''}`);
    if (!confirmed) return;

    runAction(connection.socketId, () => disconnectSystemConnection(connection.socketId));
  }, [runAction]);

  const history = data?.history || [];
  const server = data?.server || {};
  const api = data?.api || {};
  const socket = data?.socket || {};
  const mongo = data?.mongo || {};
  const backup = data?.backup || {};
  const presence = data?.presence || {};
  const summary = presence.summary || {};

  const endpointRows = useMemo(() => (api.topEndpoints || []).map((row) => ({
    ...row,
    key: `${row.method}:${row.endpoint}`,
  })), [api.topEndpoints]);

  const auditRows = useMemo(() => (data?.audit || []).map((row) => ({
    ...row,
    key: row._id,
  })), [data?.audit]);

  const loadUserRows = useMemo(() => {
    const rows = new Map();

    (api.topUsers || []).forEach((item) => {
      if (!item.userId) return;
      rows.set(String(item.userId), {
        key: String(item.userId),
        userId: String(item.userId),
        name: item.name || item.email || 'Пользователь',
        email: item.email || '',
        role: item.role || '',
        apiRequests: Number(item.requests || 0),
        apiErrors: Number(item.errors || 0),
        apiAvgMs: Number(item.avgMs || 0),
        apiP95Ms: Number(item.p95Ms || 0),
        socketEvents: 0,
      });
    });

    (socket.topUsers || []).forEach((item) => {
      if (!item.userId) return;
      const key = String(item.userId);
      const previous = rows.get(key) || {
        key,
        userId: key,
        name: item.name || item.email || 'Пользователь',
        email: item.email || '',
        role: item.role || '',
        apiRequests: 0,
        apiErrors: 0,
        apiAvgMs: 0,
        apiP95Ms: 0,
        socketEvents: 0,
      };

      rows.set(key, {
        ...previous,
        name: previous.name || item.name || item.email || 'Пользователь',
        email: previous.email || item.email || '',
        role: previous.role || item.role || '',
        socketEvents: Number(item.count || 0),
      });
    });

    return Array.from(rows.values())
      .sort((left, right) => (
        (right.apiRequests + right.socketEvents) - (left.apiRequests + left.socketEvents)
      ))
      .slice(0, 20);
  }, [api.topUsers, socket.topUsers]);

  const backupTone = !backup.configured || backup.stale ? 'bad' : 'good';
  const mongoTone = mongo.connected ? 'good' : 'bad';

  return (
    <Layout sidebar backButton>
      <div className="systemMonitor">
        <div className="systemMonitor__hero">
          <div>
            <div className="systemMonitor__eyebrow">PRODUCTION · SYSTEM MONITOR</div>
            <h1>Мониторинг системы</h1>
            <p>
              VPS, Node/PM2, MongoDB, API, Socket.IO, пользователи online и резервные копии.
              Секреты, JWT и содержимое запросов в мониторинг не попадают.
            </p>
          </div>

          <div className="systemMonitor__heroActions">
            <div className={`systemMonitor__livePill ${error ? 'is-bad' : 'is-good'}`}>
              <i /> {error ? 'ERROR' : 'LIVE'}
            </div>
            <select
              value={historyMinutes}
              onChange={(event) => setHistoryMinutes(Number(event.target.value))}
              className="systemMonitor__select"
            >
              <option value={15}>15 минут</option>
              <option value={60}>1 час</option>
              <option value={360}>6 часов</option>
              <option value={1440}>24 часа</option>
            </select>
            <button
              type="button"
              className="systemMonitor__controlBtn"
              onClick={() => setPaused((value) => !value)}
            >
              {paused ? 'Продолжить' : 'Пауза'}
            </button>
            <button
              type="button"
              className="systemMonitor__controlBtn is-primary"
              onClick={() => load()}
            >
              Обновить
            </button>
          </div>
        </div>

        {error ? <div className="systemMonitor__error">{error}</div> : null}
        {loading && !data ? <div className="systemMonitor__loading">Загрузка системных метрик…</div> : null}

        {data ? (
          <>
            <div className="systemMonitor__metaLine">
              Последнее обновление: {formatDate(data.generatedAt)} · автообновление {paused ? 'на паузе' : 'каждые 3 сек'}
            </div>

            <div className="systemMonitor__statsGrid">
              <StatCard label="Пользователи online" value={summary.usersOnline || 0} sub={`гостей ${summary.guestsOnline || 0}`} tone="good" />
              <StatCard label="Sockets" value={summary.socketsOnline || 0} sub={`FRONT ${summary.frontSockets || 0} · ADMIN ${summary.adminSockets || 0}`} />
              <StatCard label="CPU VPS" value={`${Number(server.cpuPercent || 0).toFixed(1)}%`} sub={`process ${Number(server.processCpuPercent || 0).toFixed(1)}%`} tone={server.cpuPercent > 85 ? 'bad' : server.cpuPercent > 65 ? 'warn' : 'good'} />
              <StatCard label="RAM VPS" value={`${Number(server.memory?.usedPercent || 0).toFixed(1)}%`} sub={`${formatBytes(server.memory?.usedBytes)} / ${formatBytes(server.memory?.totalBytes)}`} tone={server.memory?.usedPercent > 90 ? 'bad' : server.memory?.usedPercent > 75 ? 'warn' : 'good'} />
              <StatCard label="Disk" value={server.disk ? `${Number(server.disk.usedPercent || 0).toFixed(0)}%` : '—'} sub={server.disk ? `${formatBytes(server.disk.availableBytes)} свободно` : 'df недоступен'} tone={server.disk?.usedPercent > 90 ? 'bad' : server.disk?.usedPercent > 75 ? 'warn' : 'good'} />
              <StatCard label="API" value={`${Number(api.requestsPerMinute || 0).toFixed(1)}/мин`} sub={`avg ${Number(api.avgMs || 0).toFixed(1)} ms · p95 ${Number(api.p95Ms || 0).toFixed(1)} ms`} tone={api.status?.server5xx > 0 ? 'warn' : 'good'} />
              <StatCard label="Socket events" value={`${Number(socket.eventsPerMinute || 0).toFixed(1)}/мин`} sub={`${summary.clientsOnline || 0} clients`} />
              <StatCard label="MongoDB" value={mongo.connected ? 'ONLINE' : 'OFFLINE'} sub={mongo.pingMs !== null ? `${mongo.pingMs} ms · ${mongo.database || ''}` : (mongo.state || '—')} tone={mongoTone} />
              <StatCard label="Backup" value={backup.configured ? (backup.stale ? 'STALE' : 'OK') : 'NO BACKUP'} sub={backup.lastSuccessAt ? formatAgoSeconds(backup.ageSeconds) : 'копий пока нет'} tone={backupTone} />
              <StatCard label="Network ↓" value={formatRate(server.network?.rxBytesPerSecond || 0)} sub={`↑ ${formatRate(server.network?.txBytesPerSecond || 0)}`} />
            </div>

            <div className="systemMonitor__chartsGrid">
              <SystemMonitorChart
                title="CPU / RAM, %"
                history={history}
                series={[
                  { key: 'cpuPercent', label: 'CPU VPS' },
                  { key: 'ramPercent', label: 'RAM VPS' },
                  { key: 'processCpuPercent', label: 'CPU Node' },
                ]}
              />
              <SystemMonitorChart
                title="API"
                history={history}
                series={[
                  { key: 'apiRequestsPerMinute', label: 'requests/min' },
                  { key: 'apiAvgMs', label: 'avg ms' },
                  { key: 'apiP95Ms', label: 'p95 ms' },
                ]}
              />
              <SystemMonitorChart
                title="Online / Socket.IO"
                history={history}
                series={[
                  { key: 'usersOnline', label: 'users' },
                  { key: 'guestsOnline', label: 'guests' },
                  { key: 'socketsOnline', label: 'sockets' },
                  { key: 'socketEventsPerMinute', label: 'events/min' },
                ]}
              />
              <SystemMonitorChart
                title="Network, KB/s"
                history={history.map((item) => ({
                  ...item,
                  networkRxKb: Number(item.rxBytesPerSecond || 0) / 1024,
                  networkTxKb: Number(item.txBytesPerSecond || 0) / 1024,
                }))}
                series={[
                  { key: 'networkRxKb', label: 'RX KB/s' },
                  { key: 'networkTxKb', label: 'TX KB/s' },
                ]}
              />
            </div>

            <div className="systemMonitor__detailsGrid">
              <section className="systemMonitor__section">
                <div className="systemMonitor__sectionTitle">Backend / VPS</div>
                <div className="systemMonitor__details">
                  <DetailRow label="Node" value={server.nodeVersion || '—'} />
                  <DetailRow label="Backend version" value={server.backendVersion || '—'} />
                  <DetailRow label="Git" value={[server.git?.branch, server.git?.commit].filter(Boolean).join(' · ') || '—'} />
                  <DetailRow label="PID / PORT" value={`${server.pid || '—'} / ${server.port || '—'}`} />
                  <DetailRow label="VPS uptime" value={formatDuration(server.uptimeSeconds)} />
                  <DetailRow label="Node uptime" value={formatDuration(server.processUptimeSeconds)} />
                  <DetailRow label="Node RSS" value={formatBytes(server.processMemory?.rssBytes)} />
                  <DetailRow label="Load average" value={(server.loadAverage || []).join(' · ') || '—'} />
                  <DetailRow label="Nginx" value={`${server.nginx?.status || '—'}${server.nginx?.version ? ` · ${server.nginx.version}` : ''}`} tone={statusTone(server.nginx?.status === 'active')} />
                  <DetailRow label="TLS" value={server.tls?.daysRemaining !== null && server.tls?.daysRemaining !== undefined ? `${server.tls.daysRemaining} дней · до ${formatDate(server.tls.validTo)}` : '—'} tone={server.tls?.daysRemaining < 15 ? 'warn' : undefined} />
                </div>
              </section>

              <section className="systemMonitor__section">
                <div className="systemMonitor__sectionTitle">MongoDB</div>
                <div className="systemMonitor__details">
                  <DetailRow label="Состояние" value={mongo.connected ? 'ONLINE' : String(mongo.state || 'OFFLINE').toUpperCase()} tone={mongoTone} />
                  <DetailRow label="Ping" value={mongo.pingMs !== null ? `${mongo.pingMs} ms` : '—'} />
                  <DetailRow label="Database" value={mongo.database || '—'} />
                  <DetailRow label="Version" value={mongo.version || 'нет прав buildInfo'} />
                  <DetailRow label="Collections" value={mongo.collections ?? '—'} />
                  <DetailRow label="Documents" value={mongo.documents ?? '—'} />
                  <DetailRow label="Logical data" value={mongo.dataSizeBytes !== null ? formatBytes(mongo.dataSizeBytes) : '—'} />
                  <DetailRow label="Storage" value={mongo.storageSizeBytes !== null ? formatBytes(mongo.storageSizeBytes) : '—'} />
                  <DetailRow label="Indexes" value={mongo.indexSizeBytes !== null ? formatBytes(mongo.indexSizeBytes) : '—'} />
                  <DetailRow label="Connections" value={mongo.connectionStatsAvailable ? mongo.connections : 'нет clusterMonitor — не повышаем права'} />
                </div>
              </section>

              <section className="systemMonitor__section">
                <div className="systemMonitor__sectionTitle">Backups</div>
                <div className="systemMonitor__details">
                  <DetailRow label="Состояние" value={!backup.configured ? 'НЕ НАСТРОЕНО' : backup.stale ? 'ПРОСРОЧЕН' : 'OK'} tone={backupTone} />
                  <DetailRow label="Timer" value={backup.timerStatus || '—'} tone={backup.timerStatus === 'active' ? 'good' : 'warn'} />
                  <DetailRow label="Последняя копия" value={backup.lastSuccessAt ? formatDate(backup.lastSuccessAt) : '—'} />
                  <DetailRow label="Возраст" value={formatAgoSeconds(backup.ageSeconds)} />
                  <DetailRow label="Размер последней" value={formatBytes(backup.lastSizeBytes)} />
                  <DetailRow label="Hourly" value={backup.hourlyCount ?? 0} />
                  <DetailRow label="Daily" value={backup.dailyCount ?? 0} />
                  <DetailRow label="Всего" value={`${backup.totalCount ?? 0} · ${formatBytes(backup.totalBytes)}`} />
                  <DetailRow label="SHA256 рядом" value={backup.verified ? 'есть' : 'нет / не найден'} tone={backup.verified ? 'good' : 'warn'} />
                  <DetailRow label="Blocked users" value={data.blockedUsers ?? 0} />
                </div>
              </section>

              <section className="systemMonitor__section">
                <div className="systemMonitor__sectionTitle">PM2 processes</div>
                <div className="systemMonitor__pm2List">
                  {(server.pm2?.processes || []).map((processItem) => (
                    <div className="systemMonitor__pm2Row" key={`${processItem.pmId}-${processItem.name}`}>
                      <div>
                        <strong>{processItem.name || `pm2 #${processItem.pmId}`}</strong>
                        <span>PID {processItem.pid || '—'} · restarts {processItem.restarts || 0}</span>
                      </div>
                      <div>
                        <b>{Number(processItem.cpuPercent || 0).toFixed(1)}%</b>
                        <span>{formatBytes(processItem.memoryBytes)}</span>
                      </div>
                      <em className={processItem.status === 'online' ? 'is-good' : 'is-bad'}>{processItem.status}</em>
                    </div>
                  ))}
                  {!(server.pm2?.processes || []).length ? <div className="systemMonitor__empty">PM2 jlist недоступен.</div> : null}
                </div>
              </section>
            </div>

            <SystemMonitorUsers
              users={presence.users || []}
              guests={presence.guests || []}
              currentUserId={currentUserId}
              onDisconnectUser={handleDisconnectUser}
              onDisconnectConnection={handleDisconnectConnection}
              onBlock={handleBlock}
              onUnblock={handleUnblock}
              busyKey={busyKey}
            />

            <TopTable
              title="Нагрузка по авторизованным пользователям"
              rows={loadUserRows}
              empty="За выбранные окна активности авторизованных пользователей пока нет."
              columns={[
                {
                  key: 'name',
                  label: 'Пользователь',
                  render: (row) => (
                    <div className="systemMonitor__userLoadIdentity">
                      <strong>{row.name || 'Пользователь'}</strong>
                      <span>{[row.email, row.role].filter(Boolean).join(' · ') || row.userId}</span>
                    </div>
                  ),
                },
                { key: 'apiRequests', label: 'API / 5 мин' },
                { key: 'apiAvgMs', label: 'AVG', render: (row) => `${row.apiAvgMs} ms` },
                { key: 'apiP95Ms', label: 'P95', render: (row) => `${row.apiP95Ms} ms` },
                { key: 'apiErrors', label: 'Ошибки API' },
                { key: 'socketEvents', label: 'Socket / 1 мин' },
              ]}
            />

            <div className="systemMonitor__tablesGrid">
              <TopTable
                title="TOP API · последние 5 минут"
                rows={endpointRows}
                columns={[
                  { key: 'route', label: 'Endpoint', mono: true, render: (row) => `${row.method} ${row.endpoint}` },
                  { key: 'requests', label: 'Req' },
                  { key: 'avgMs', label: 'AVG', render: (row) => `${row.avgMs} ms` },
                  { key: 'p95Ms', label: 'P95', render: (row) => `${row.p95Ms} ms` },
                  { key: 'errors', label: 'Errors' },
                ]}
              />

              <TopTable
                title="TOP Socket.IO events · последняя минута"
                rows={(socket.topEvents || []).map((row) => ({ ...row, key: row.name }))}
                columns={[
                  { key: 'name', label: 'Namespace:event', mono: true },
                  { key: 'count', label: 'Events' },
                ]}
              />
            </div>

            <TopTable
              title="Медленные API-запросы · ≥500 ms"
              rows={(api.slowRequests || []).map((row, index) => ({ ...row, key: `${row.ts}-${index}` }))}
              columns={[
                { key: 'time', label: 'Время', render: (row) => new Date(row.ts).toLocaleTimeString('ru-RU') },
                { key: 'endpoint', label: 'Endpoint', mono: true, render: (row) => `${row.method} ${row.endpoint}` },
                { key: 'durationMs', label: 'Duration', render: (row) => `${row.durationMs} ms` },
                { key: 'status', label: 'HTTP' },
                { key: 'userId', label: 'User', mono: true, render: (row) => row.userId || 'guest/system' },
              ]}
            />

            <TopTable
              title="Security / audit"
              rows={auditRows}
              columns={[
                { key: 'createdAt', label: 'Время', render: (row) => formatDate(row.createdAt) },
                { key: 'actor', label: 'Кто', render: (row) => row.actor?.name || row.actor?.email || '—' },
                { key: 'action', label: 'Действие', mono: true },
                { key: 'target', label: 'Цель', render: (row) => row.target?.name || row.target?.email || row.socketId || '—' },
                { key: 'disconnectedCount', label: 'Sockets' },
                { key: 'reason', label: 'Причина', render: (row) => row.reason || '—' },
              ]}
            />
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default SystemMonitorPage;
